import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Zap, Globe, Sparkles, Crown, Activity, RefreshCw, ChevronDown, Cpu, Library, Languages } from 'lucide-react'
import VoiceLibraryModal from './VoiceLibraryModal'
import TelnyxVoiceLibraryModal from './TelnyxVoiceLibraryModal'
import { SettingLabel, RecommendedBaseline, VoiceEngineTooltipScope, LAW_FIRM_BASELINE, PRESETS, type PresetId } from './VoiceEngineHelpers'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { useToast } from '@/hooks/use-toast'
import api from '@/lib/api'

interface CustomLlmProviderRow {
  id: string
  name: string
  baseUrl: string
  defaultModelId: string
}

interface IntegrationSecretRow {
  id: string
  identifier: string
  type: string
}

// Bundled models that require a top-level llm_api_key_ref. Mirror of
// BYOK_BUNDLED_MODELS in packages/api/src/config/telnyxDefaults.ts.
const BYOK_BUNDLED_MODELS = ['google/gemini-2.5-flash']
const isBundledBYOK = (model: string) => BYOK_BUNDLED_MODELS.includes(model)

// Only the voices we've verified work end-to-end against Telnyx with our
// ElevenLabs integration. Matches TELNYX_VOICE_ID_MAP on the backend exactly.
// For anything else, use the "Custom Telnyx Voice String" field — paste the
// full string directly from the Telnyx portal.
const VOICE_OPTIONS = [
  // Rime ArcanaV3 — purpose-built for multilingual code-switching across 10
  // languages (English, Spanish, French, German, Hebrew, Hindi, Japanese,
  // Portuguese, Arabic, Tamil). Pick these when callers may speak more than
  // English. Each Rime voice can speak every supported language.
  { id: 'rime_luna', name: 'Luna (Rime)', description: '⭐ Multilingual code-switching — recommended for ES/EN agents' },
  { id: 'rime_astra', name: 'Astra (Rime)', description: 'Multilingual code-switching' },
  { id: 'rime_celeste', name: 'Celeste (Rime)', description: 'Multilingual code-switching' },
  { id: 'rime_nova', name: 'Nova (Rime)', description: 'Multilingual code-switching' },
  { id: 'rime_solstice', name: 'Solstice (Rime)', description: 'Multilingual code-switching' },
  // ElevenLabs — best for single-language English agents, premium voice quality.
  { id: 'molly', name: 'Molly (ElevenLabs)', description: 'Warm, professional female (legacy default)' },
  { id: 'lauren', name: 'Lauren (ElevenLabs)', description: 'Friendly, comforting and soft' },
  { id: 'nora', name: 'Nora (ElevenLabs)', description: 'Calm, clear, and natural' },
  { id: 'sarah', name: 'Sarah (ElevenLabs)', description: 'Versatile professional female (flagship)' },
  { id: 'charlotte', name: 'Charlotte (ElevenLabs)', description: 'Calm British female' },
  { id: 'brian', name: 'Brian (ElevenLabs)', description: 'Warm professional male' },
  { id: 'aria', name: 'Aria (ElevenLabs)', description: 'Newer flagship female' },
  // Telnyx native fallback (works without ElevenLabs key).
  { id: 'quinn', name: 'Quinn (Telnyx native)', description: 'Calm, authoritative — no ElevenLabs key needed' },
]

const TTS_MODEL_OPTIONS = [
  { id: 'eleven_flash_v2', name: 'Flash v2', description: 'Lowest latency (~75ms), English-only' },
  { id: 'eleven_flash_v2_5', name: 'Flash v2.5', description: '32 languages, slightly higher latency' },
  { id: 'eleven_turbo_v2_5', name: 'Turbo v2.5', description: 'Multilingual, higher quality' },
]

// Caller speech recognition. Verified via direct PATCH probe 2026-04-27.
// For multilingual / code-switching, Telnyx specifically recommends
// whisper-large-v3-turbo. flux is English-only (will misfire on Spanish).
const TRANSCRIPTION_OPTIONS = [
  { id: 'openai/whisper-large-v3-turbo', name: 'Whisper Large v3 Turbo', description: '⭐ Multilingual — Telnyx-recommended for ES/EN code-switching' },
  { id: 'deepgram/nova-3', name: 'Deepgram Nova 3', description: 'Multilingual + auto-detect — strong on accents' },
  { id: 'deepgram/nova-2', name: 'Deepgram Nova 2', description: 'Reliable older flagship (English)' },
  { id: 'azure/fast', name: 'Azure Fast', description: 'Multilingual via Azure' },
  { id: 'openai/whisper-tiny', name: 'Whisper Tiny', description: 'Smallest Whisper — fastest, less accurate' },
  { id: 'deepgram/flux', name: 'Deepgram Flux', description: 'English-only — fastest (~50ms). Avoid for multilingual.' },
]

interface RecentCallAttribution {
  callId: string
  startedAt: string
  durationSeconds: number | null
  phoneFrom: string | null
  assistantName: string | null
  modelUsed: string | null
  modelKind: 'bundled' | 'custom_llm' | 'unknown'
  llmTtftMs: number | null
}

// Voices that once appeared in the dropdown but all collapsed to Lauren on
// the backend. If we encounter one in a stored record, snap to the new
// default on load and warn the user. 'molly' was previously in this list but
// is now a real first-class alias as of 2026-04-25 — do NOT add it back.
// 'rachel' / 'bella' were also legacy aliases but the new sarah/charlotte/
// brian/aria additions are first-class — they map to real distinct voices.
const LEGACY_ALIASES = new Set(['adam', 'jessica', 'rachel', 'bella', 'emily', 'alexandra', 'sophia', 'marcus', 'brooke'])

// Friendly name for the active voice family, used to explain why a knob
// is dimmed. Mirrors the voiceFamily memo in the component below.
type VoiceFamily = 'elevenlabs' | 'rime' | 'telnyx_ultra' | 'telnyx_native' | 'azure' | 'aws' | 'inworld' | 'minimax' | 'resemble' | 'xai' | 'unknown'
const VOICE_FAMILY_LABELS: Record<VoiceFamily, string> = {
  elevenlabs: 'ElevenLabs',
  rime: 'Rime',
  telnyx_ultra: 'Telnyx Ultra',
  telnyx_native: 'Telnyx native (NaturalHD/Natural/KokoroTTS)',
  azure: 'Azure',
  aws: 'AWS Polly',
  inworld: 'Inworld',
  minimax: 'Minimax',
  resemble: 'Resemble',
  xai: 'xAI',
  unknown: 'unknown provider',
}

/**
 * Reusable "this control is ignored for the active voice" callout. Used
 * under TTS Model + Stability + Similarity Boost + Voice Style sliders —
 * all of which are wired ONLY to ElevenLabs voice_settings inside
 * buildVoiceSettings() and are silently dropped for any other family.
 */
function NonElevenLabsNotice({
  family,
  customVoice,
  controlLabel,
}: {
  family: VoiceFamily
  customVoice: string
  controlLabel: string
}) {
  const familyLabel = VOICE_FAMILY_LABELS[family]
  return (
    <p className="text-[10px] text-yellow-700 dark:text-yellow-400 leading-relaxed">
      ⚠ Ignored — active voice is{' '}
      {customVoice ? <code className="font-mono">{customVoice}</code> : `a ${familyLabel} voice`}
      {customVoice && ` (${familyLabel})`}.{' '}
      The {controlLabel} above is preserved on save but has no effect on calls — it's an ElevenLabs-only field.
      {customVoice ? ' Clear the custom voice or pick an ElevenLabs voice to re-enable.' : ' Pick an ElevenLabs voice to re-enable.'}
    </p>
  )
}

// Only fields that actually reach live Telnyx agents live here. The old
// form had ~13 fields; 8 of them were dead knobs from the dormant ElevenLabs
// conversational AI path. Keeping them visible invited admins to "tune"
// settings that did nothing, so they've been removed from the UI entirely.
// Defaults match packages/api/src/config/telnyxDefaults.ts::buildVoiceSettings
// so the form reflects what's actually running in Telnyx when unset.
const DEFAULT_FORM = {
  voiceId: 'molly',
  ttsModel: 'eleven_flash_v2',       // → ElevenLabs.<this>.<voiceId> in Telnyx voice string
  llmModel: 'moonshotai/Kimi-K2.5',  // K2.6 verified unsupported by Telnyx AI Assistants (error 10027, 2026-04-23)
  stability: 0.5,                    // → voice_settings.temperature
  similarityBoost: 0.55,             // → voice_settings.similarity_boost
  voiceSpeed: 1.0,                   // → voice_settings.voice_speed
  voiceStyle: 0.0,                   // → voice_settings.style (0-1 expressiveness)
  // null = bundled model from llmModel above; UUID = use the named provider
  // via Telnyx external_llm. Org-level — applies to every assistant in the org.
  customLlmProviderId: null as string | null,
  // BYOK bundled — Telnyx Integration Secret identifier holding the upstream
  // provider's API key. Only consulted when llmModel is in BYOK_BUNDLED_MODELS
  // (currently just google/gemini-2.5-flash). null when not needed.
  bundledLlmApiKeyRef: null as string | null,
  // Interruption tuning — direct mapping to Telnyx interruption_settings.
  // interruptionEnabled is the master switch — when false the three timing
  // sliders are sent but ignored by Telnyx (interruption disabled entirely).
  interruptionEnabled: true,
  interruptionWaitSeconds: 1.0,
  interruptionOnPunctuationSeconds: 0.1,
  interruptionOnNoPunctuationSeconds: 1.5,
  // ASR + telephony fine-tuning
  transcriptionModel: 'deepgram/flux',
  // Transcription language. 'auto' = detect each utterance (required for
  // ES/EN code-switching). 'en' or 'es' lock to one language (slightly
  // faster, fewer false positives if you ONLY take that language's calls).
  transcriptionLanguage: 'auto' as 'auto' | 'en' | 'es',
  userIdleTimeoutSeconds: 90,        // when AI ends call on silent caller
  userIdleReplySeconds: 15,          // when AI prompts silent caller
  timeLimitSeconds: 1800,            // max call duration safety net (30 min)
  crossCallMemoryEnabled: true,      // remember returning callers by phone
}

interface VoiceEngineSectionProps {
  orgId: string
}

export default function VoiceEngineSection({ orgId }: VoiceEngineSectionProps) {
  const { toast } = useToast()
  const [form, setForm] = useState({ ...DEFAULT_FORM })
  const [activePreset, setActivePreset] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isOpen, setIsOpen] = useState(true)
  const [legacyVoiceWarning, setLegacyVoiceWarning] = useState<string | null>(null)
  const [customVoiceInput, setCustomVoiceInput] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [liveConfig, setLiveConfig] = useState<any[] | null>(null)
  const [customLlmProviders, setCustomLlmProviders] = useState<CustomLlmProviderRow[]>([])
  const [integrationSecrets, setIntegrationSecrets] = useState<IntegrationSecretRow[]>([])
  const [recentCalls, setRecentCalls] = useState<RecentCallAttribution[] | null>(null)
  const [isLoadingRecentCalls, setIsLoadingRecentCalls] = useState(false)
  const [voiceLibraryOpen, setVoiceLibraryOpen] = useState(false)
  const [telnyxLibraryOpen, setTelnyxLibraryOpen] = useState(false)

  // Resolve which TTS engine the active voice runs on, so we can correctly
  // dim runtime-irrelevant knobs. The custom-voice field overrides the
  // dropdown alias (matches save() precedence) and the dropdown alias
  // resolves through TELNYX_VOICE_ID_MAP — molly/lauren/etc → ElevenLabs,
  // rime_* → Rime, quinn → Telnyx.Ultra. Anything else (full Telnyx voice
  // strings the admin pasted or picked from the library) gets routed by
  // its provider prefix.
  //
  // Why it matters: stability, similarityBoost, and voiceStyle are wired
  // ONLY to ElevenLabs voice_settings inside buildVoiceSettings()
  // (telnyxDefaults.ts). For Telnyx native / Rime / AWS / Azure voices,
  // those form fields are silently dropped at apply-time. Without dimming,
  // an admin tuning Stability for a NaturalHD voice has no idea their
  // changes aren't reaching Telnyx.
  const voiceFamily = useMemo<VoiceFamily>(() => {
    const custom = customVoiceInput.trim()
    const effective = custom || form.voiceId
    const lower = effective.toLowerCase()
    if (!effective) return 'unknown'
    // Aliases — resolve to their underlying engine.
    if (['molly','lauren','nora','sarah','charlotte','brian','aria'].includes(effective)) return 'elevenlabs'
    if (effective.startsWith('rime_')) return 'rime'
    if (effective === 'quinn') return 'telnyx_ultra'
    // Full provider-prefixed voice strings.
    if (lower.startsWith('elevenlabs.')) return 'elevenlabs'
    if (lower.startsWith('rime.')) return 'rime'
    if (lower.startsWith('telnyx.ultra.')) return 'telnyx_ultra'
    if (lower.startsWith('telnyx.')) return 'telnyx_native'
    if (lower.startsWith('azure.')) return 'azure'
    if (lower.startsWith('aws.')) return 'aws'
    if (lower.startsWith('inworld.')) return 'inworld'
    if (lower.startsWith('minimax.')) return 'minimax'
    if (lower.startsWith('resemble.')) return 'resemble'
    if (lower.startsWith('xai.')) return 'xai'
    return 'unknown'
  }, [form.voiceId, customVoiceInput])

  // ElevenLabs-only knobs: stability (→ temperature), similarityBoost,
  // voiceStyle. Telnyx.Ultra accepts none of these (uses expressive_mode
  // which we hardcode true). All other providers also drop these.
  const isElevenLabsActive = voiceFamily === 'elevenlabs'

  useEffect(() => {
    loadSettings()
  }, [orgId])

  const loadSettings = async () => {
    setIsLoading(true)
    try {
      // Load voice optimization + custom-LLM providers + Telnyx Integration
      // Secrets in parallel. Providers are for the Custom LLM routing path;
      // secrets power the BYOK-bundled picker (e.g. Google key for Gemini).
      const [voResponse, providersResponse, secretsResponse] = await Promise.all([
        api.get(`/api/platform/organizations/${orgId}/voice-optimization`),
        api.get(`/api/platform/custom-llm-providers`).catch(() => ({ data: { data: [] } })),
        api.get(`/api/platform/integration-secrets`).catch(() => ({ data: { data: [] } })),
      ])
      setCustomLlmProviders(providersResponse.data?.data || [])
      setIntegrationSecrets(secretsResponse.data?.data || [])
      const response = voResponse
      if (response.data.success) {
        const vo = response.data.voiceOptimization || {}
        // Legacy fields (ttsModel, optimizeStreamingLatency, etc.) may still
        // exist on stored records from the old UI — we intentionally don't
        // read them into form state, but the backend preserves them on save
        // via the { ...existing, ...value } merge, so nothing breaks.
        // llmModel back-compat: if an old record has an ElevenLabs-conversational
        // model ID (e.g. "gpt-4o-mini" without publisher prefix), fall back to
        // the Telnyx default rather than showing a broken dropdown value.
        // Kimi K2.6 auto-heal: if the org has K2.6 saved from an earlier
        // build (before we verified Telnyx AI Assistants rejects it), show
        // K2.5 instead. Backend validator rejects K2.6 on save too, so if we
        // didn't map it here the user would hit a save error on an unchanged
        // form. See telnyxDefaults.ts for the error-10027 context.
        let incomingLlm: string | undefined = vo.llmModel
        if (incomingLlm === 'moonshotai/Kimi-K2.6') incomingLlm = 'moonshotai/Kimi-K2.5'
        // gpt-4o-mini auto-heal: Telnyx pulled it from AI Assistants on/
        // before 2026-04-26 ("Model openai/gpt-4o-mini is not available
        // for AI Assistants"). Snap to gpt-4o so the user doesn't hit a
        // save error on an unchanged form. The backend validator no longer
        // accepts gpt-4o-mini for the prefixed slug either.
        if (incomingLlm === 'openai/gpt-4o-mini') incomingLlm = 'openai/gpt-4o'
        // claude-opus-4-6 auto-heal: also pulled by Telnyx. Snap to
        // claude-haiku-4-5 (same provider, still recommended).
        if (incomingLlm === 'anthropic/claude-opus-4-6') incomingLlm = 'anthropic/claude-haiku-4-5'
        const isTelnyxSlug = typeof incomingLlm === 'string' && incomingLlm.includes('/')

        // voiceId back-compat: if the stored value is one of the old
        // dropdown aliases that used to collapse to Lauren, snap the form to
        // lauren and warn. If it's a full Telnyx voice string, surface it in
        // the Custom field so the user can see what's currently configured.
        let resolvedVoiceId: string = DEFAULT_FORM.voiceId
        let warning: string | null = null
        let customInputPrefill = ''
        const stored: string | undefined = vo.voiceId
        if (typeof stored === 'string' && stored.length) {
          if (VOICE_OPTIONS.some(v => v.id === stored)) {
            resolvedVoiceId = stored
          } else if (LEGACY_ALIASES.has(stored)) {
            resolvedVoiceId = 'molly'
            warning = `Voice "${stored}" is no longer supported — was always resolving to Lauren on the backend. Switched to Molly; save to clear.`
          } else if (stored.includes('.')) {
            // Full Telnyx voice string in the Custom field.
            resolvedVoiceId = stored
            customInputPrefill = stored
          } else {
            resolvedVoiceId = 'molly'
            warning = `Stored voice "${stored}" isn't recognized — defaulted to Molly. Pick a voice below and save.`
          }
        }

        setForm({
          voiceId: resolvedVoiceId,
          ttsModel: typeof vo.ttsModel === 'string' && TTS_MODEL_OPTIONS.some(m => m.id === vo.ttsModel) ? vo.ttsModel : DEFAULT_FORM.ttsModel,
          llmModel: isTelnyxSlug ? incomingLlm! : DEFAULT_FORM.llmModel,
          stability: vo.stability ?? DEFAULT_FORM.stability,
          similarityBoost: vo.similarityBoost ?? DEFAULT_FORM.similarityBoost,
          voiceSpeed: vo.voiceSpeed ?? DEFAULT_FORM.voiceSpeed,
          voiceStyle: vo.voiceStyle ?? DEFAULT_FORM.voiceStyle,
          customLlmProviderId: typeof vo.customLlmProviderId === 'string' ? vo.customLlmProviderId : null,
          bundledLlmApiKeyRef: typeof vo.bundledLlmApiKeyRef === 'string' && vo.bundledLlmApiKeyRef.length > 0 ? vo.bundledLlmApiKeyRef : null,
          interruptionEnabled: typeof vo.interruptionEnabled === 'boolean' ? vo.interruptionEnabled : DEFAULT_FORM.interruptionEnabled,
          interruptionWaitSeconds: typeof vo.interruptionWaitSeconds === 'number' ? vo.interruptionWaitSeconds : DEFAULT_FORM.interruptionWaitSeconds,
          interruptionOnPunctuationSeconds: typeof vo.interruptionOnPunctuationSeconds === 'number' ? vo.interruptionOnPunctuationSeconds : DEFAULT_FORM.interruptionOnPunctuationSeconds,
          interruptionOnNoPunctuationSeconds: typeof vo.interruptionOnNoPunctuationSeconds === 'number' ? vo.interruptionOnNoPunctuationSeconds : DEFAULT_FORM.interruptionOnNoPunctuationSeconds,
          transcriptionModel: typeof vo.transcriptionModel === 'string' && TRANSCRIPTION_OPTIONS.some(m => m.id === vo.transcriptionModel) ? vo.transcriptionModel : DEFAULT_FORM.transcriptionModel,
          transcriptionLanguage: (typeof vo.transcriptionLanguage === 'string' && ['auto','en','es'].includes(vo.transcriptionLanguage)) ? vo.transcriptionLanguage as 'auto' | 'en' | 'es' : DEFAULT_FORM.transcriptionLanguage,
          userIdleTimeoutSeconds: typeof vo.userIdleTimeoutSeconds === 'number' ? vo.userIdleTimeoutSeconds : DEFAULT_FORM.userIdleTimeoutSeconds,
          userIdleReplySeconds: typeof vo.userIdleReplySeconds === 'number' ? vo.userIdleReplySeconds : DEFAULT_FORM.userIdleReplySeconds,
          timeLimitSeconds: typeof vo.timeLimitSeconds === 'number' ? vo.timeLimitSeconds : DEFAULT_FORM.timeLimitSeconds,
          crossCallMemoryEnabled: typeof vo.crossCallMemoryEnabled === 'boolean' ? vo.crossCallMemoryEnabled : DEFAULT_FORM.crossCallMemoryEnabled,
        })
        setLegacyVoiceWarning(warning)
        setCustomVoiceInput(customInputPrefill)
      }
    } catch (error) {
      console.error('Failed to load voice optimization:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Fetches the last 10 calls for this org with model attribution + LLM TTFT.
  // Lets the admin compare bundled vs custom-LLM perf side-by-side without
  // jumping between dashboards.
  const loadRecentCalls = async () => {
    setIsLoadingRecentCalls(true)
    try {
      const response = await api.get(`/api/platform/organizations/${orgId}/voice/recent-calls-attribution?limit=10`)
      if (response.data?.success) {
        setRecentCalls(response.data.data || [])
      } else {
        toast({
          title: 'Could not fetch recent calls',
          description: response.data?.error || 'Unknown error',
          variant: 'destructive',
        })
      }
    } catch (error: any) {
      toast({
        title: 'Failed to fetch recent calls',
        description: error?.response?.data?.error || error?.message || 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setIsLoadingRecentCalls(false)
    }
  }

  // Fetches the live config directly from Telnyx (not our DB) for every
  // Telnyx-registered agent in this org. Use this to verify end-to-end that
  // a save actually took effect, or to answer "what model is Telnyx really
  // using right now?" without tailing logs.
  const verifyLiveConfig = async () => {
    setIsVerifying(true)
    setLiveConfig(null)
    try {
      const response = await api.get(`/api/platform/organizations/${orgId}/voice/live-telnyx-config`)
      if (response.data?.success) {
        setLiveConfig(response.data.agents)
      } else {
        toast({
          title: 'Could not fetch Telnyx state',
          description: response.data?.error || 'Unknown error',
          variant: 'destructive',
        })
      }
    } catch (error: any) {
      toast({
        title: 'Verification failed',
        description: error?.response?.data?.error || error?.message || 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setIsVerifying(false)
    }
  }

  const save = async () => {
    // Validate custom voice string client-side so we don't bother the server
    // with something we already know it'll reject.
    if (customVoiceInput && customVoiceInput.trim().length > 0) {
      const trimmed = customVoiceInput.trim()
      // Mirror of the backend voiceId validator. Azure IDs are two-part
      // (Azure.<voice>); everyone else is three-part (Provider.Model.id).
      const isAzure = /^Azure\./i.test(trimmed) && trimmed.split('.').length >= 2
      const isTriple = /^(ElevenLabs|Telnyx|Rime|Inworld|AWS|Minimax|Resemble|XAI)\./i.test(trimmed) && trimmed.split('.').length >= 3
      const ok = isAzure || isTriple
      if (!ok) {
        toast({
          title: 'Invalid custom voice string',
          description: 'Must look like "Telnyx.NaturalHD.celeste", "ElevenLabs.eleven_flash_v2.XXXX", "Rime.ArcanaV3.luna", "AWS.Polly.Danielle-Neural", or "Azure.en-US-JennyNeural" — pick one from "Browse Telnyx library" or paste the exact string from the Telnyx portal.',
          variant: 'destructive',
        })
        return
      }
    }

    // BYOK validation: bundled model that needs a Google key must have one
    // selected before save, otherwise Telnyx will 400 and the user gets a
    // confusing toast. Catch it client-side with a clear message.
    if (form.customLlmProviderId === null && isBundledBYOK(form.llmModel) && !form.bundledLlmApiKeyRef) {
      toast({
        title: 'Google API Integration Secret required',
        description: `${form.llmModel} needs a Telnyx Integration Secret holding your Google API key. Pick one in the BYOK section, or create a new secret via Custom LLM Providers.`,
        variant: 'destructive',
      })
      return
    }

    // If custom field is filled, it takes precedence over the dropdown.
    const payloadVoiceId = customVoiceInput.trim() || form.voiceId

    setIsSaving(true)
    try {
      // customLlmProviderId is included unconditionally — null tells the
      // backend to revert all of this org's assistants to the bundled
      // llmModel; a UUID assigns the named provider. This is the source-of-
      // truth signal that closes the dashboard ↔ Telnyx drift gap.
      const response = await api.put(`/api/platform/organizations/${orgId}/voice-optimization`, {
        ...form,
        voiceId: payloadVoiceId,
        customLlmProviderId: form.customLlmProviderId,
        // All knobs sent unconditionally — dashboard is source of truth, every
        // save force-pushes to Telnyx so we don't get drift from portal edits.
        ttsModel: form.ttsModel,
        interruptionEnabled: form.interruptionEnabled,
        interruptionWaitSeconds: form.interruptionWaitSeconds,
        interruptionOnPunctuationSeconds: form.interruptionOnPunctuationSeconds,
        interruptionOnNoPunctuationSeconds: form.interruptionOnNoPunctuationSeconds,
        transcriptionModel: form.transcriptionModel,
        transcriptionLanguage: form.transcriptionLanguage,
        userIdleTimeoutSeconds: form.userIdleTimeoutSeconds,
        userIdleReplySeconds: form.userIdleReplySeconds,
        timeLimitSeconds: form.timeLimitSeconds,
        voiceStyle: form.voiceStyle,
        crossCallMemoryEnabled: form.crossCallMemoryEnabled,
        // BYOK secret only relevant when the selected bundled model needs one.
        // Send '' otherwise so the backend clears any leftover ref from a
        // previous BYOK selection.
        bundledLlmApiKeyRef: isBundledBYOK(form.llmModel) ? form.bundledLlmApiKeyRef : '',
      })
      if (response.data?.success) {
        setLegacyVoiceWarning(null)
        // Clear any stale "Live Telnyx state" panel — keeping it visible after
        // a save shows pre-save values and creates the false impression that
        // the new save didn't apply. Admin clicks Verify again to re-fetch.
        setLiveConfig(null)
        toast({ title: 'Voice settings saved', description: 'Pushed to all Telnyx agents for this org.' })
      } else {
        // Shouldn't happen — 200 with success=false would be unusual — but
        // if it does, surface whatever the server told us.
        toast({
          title: 'Save did not apply',
          description: response.data?.error || 'Server returned an unexpected response.',
          variant: 'destructive',
        })
      }
    } catch (error: any) {
      // Bubble the real Telnyx error up to the toast. The backend returns
      // 502 with { success: false, error, failures: [...] } when Telnyx
      // rejects the agent update — we want that visible, not a generic
      // "Failed to save" hand-wave.
      const data = error?.response?.data
      const msg = data?.error || data?.message || error?.message || 'Unknown error'
      const firstFailure = Array.isArray(data?.failures) && data.failures.length > 0 ? data.failures[0] : null
      const detail = firstFailure
        ? `${msg} — Telnyx: ${firstFailure.error || 'no detail'}${firstFailure.status ? ` (HTTP ${firstFailure.status})` : ''}`
        : msg
      toast({
        title: 'Failed to save',
        description: detail,
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  // Production presets — each sets the FULL stack (LLM + TTS model + ASR +
  // language + voice tuning + idle + interruption). Verified 2026-04-27
  // against direct Telnyx API + real call transcripts. Single source of
  // truth lives in PRESETS (VoiceEngineHelpers.tsx) so admins, the
  // baseline callout, and the buttons all stay aligned.
  // Most presets preserve the admin's voiceId — only presets that
  // semantically imply a voice (British Pro → Charlotte) set it. When
  // they do, also clear the custom voice string so the dropdown wins.
  const applyPreset = (preset: PresetId) => {
    setActivePreset(preset)
    const cfg = PRESETS[preset].config as Record<string, any>
    if (cfg.voiceId) {
      // Full Provider.Model.id voice strings (e.g. Telnyx.NaturalHD.eris) go
      // into the custom-voice field so the "Custom voice in use" banner +
      // alias-dropdown dimming behave identically to admin pasting/picking
      // from the Telnyx library. Bare aliases (molly / rime_luna / quinn)
      // clear the custom field so the dropdown renders the alias.
      if (cfg.voiceId.includes('.')) {
        setCustomVoiceInput(cfg.voiceId)
      } else {
        setCustomVoiceInput('')
      }
    }
    setForm(prev => ({ ...prev, ...cfg }))
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <RefreshCw className="h-5 w-5 mx-auto mb-2 animate-spin opacity-40" />
          Loading voice engine settings...
        </CardContent>
      </Card>
    )
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Zap className="h-4 w-4" />
                Voice Engine
              </CardTitle>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
            <CardDescription>Voice, LLM, and voice-quality knobs — syncs live to all Telnyx agents for this org</CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-6 pt-0">
            <VoiceEngineTooltipScope>

            {/* Recommended baseline — one-click apply for law-firm receptionist
                tuning. Doesn't save automatically; user picks Apply then
                Save & Sync. Power users + new admins both benefit. */}
            <RecommendedBaseline onApply={() => {
              setForm({ ...form, ...LAW_FIRM_BASELINE })
              setCustomVoiceInput('')
              setActivePreset(null)
              toast({
                title: 'Baseline applied',
                description: 'Form updated. Click "Save & Sync All Agents" to push to Telnyx.',
              })
            }} />

            {/* Save + Presets — stacks cleanly on mobile (presets row, action
                row), inlines on md+. flex-wrap previously caused awkward mid-
                button breaks on narrow screens. */}
            <div className="flex flex-col md:flex-row md:items-center md:gap-3 gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant={activePreset === 'english_pro' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => applyPreset('english_pro')}
                  className="flex-1 md:flex-initial"
                  title={PRESETS.english_pro.description}
                >
                  <Zap className="h-3.5 w-3.5 mr-1.5" />
                  English Pro
                </Button>
                <Button
                  variant={activePreset === 'multilingual_pro' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => applyPreset('multilingual_pro')}
                  className="flex-1 md:flex-initial"
                  title={PRESETS.multilingual_pro.description}
                >
                  <Globe className="h-3.5 w-3.5 mr-1.5" />
                  ES/EN Bilingual
                </Button>
                <Button
                  variant={activePreset === 'spanish_native' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => applyPreset('spanish_native')}
                  className="flex-1 md:flex-initial"
                  title={PRESETS.spanish_native.description}
                >
                  <Languages className="h-3.5 w-3.5 mr-1.5" />
                  Spanish Native
                </Button>
                <Button
                  variant={activePreset === 'premium_quality' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => applyPreset('premium_quality')}
                  className="flex-1 md:flex-initial"
                  title={PRESETS.premium_quality.description}
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                  Premium Quality
                </Button>
                <Button
                  variant={activePreset === 'british_pro' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => applyPreset('british_pro')}
                  className="flex-1 md:flex-initial"
                  title={PRESETS.british_pro.description}
                >
                  <Crown className="h-3.5 w-3.5 mr-1.5" />
                  British Pro
                </Button>
              </div>
              <div className="md:flex-1 hidden md:block" />
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={verifyLiveConfig} disabled={isVerifying} className="flex-1 md:flex-initial">
                  {isVerifying ? 'Verifying…' : 'Verify Telnyx state'}
                </Button>
                <Button onClick={save} disabled={isSaving} size="sm" className="flex-1 md:flex-initial">
                  {isSaving ? 'Saving & Syncing...' : 'Save & Sync All Agents'}
                </Button>
              </div>
            </div>

            {/* Preset + non-ElevenLabs warning. Presets carry ElevenLabs-only
                values (stability/similarityBoost/voiceStyle/ttsModel) that are
                silently dropped for Telnyx native / Rime / AWS / Azure voices.
                Without this callout, an admin who picked a NaturalHD voice and
                clicks "Multilingual Pro" sees the buttons highlight + sliders
                snap, and reasonably assumes the whole stack moved — when only
                the LLM, ASR, idle, and Voice Speed bits actually applied. */}
            {activePreset && !isElevenLabsActive && (
              <div className="rounded-md border border-yellow-500/50 bg-yellow-500/5 px-3 py-2 text-[11px] text-yellow-800 dark:text-yellow-300 leading-relaxed">
                <span className="font-semibold">Heads up:</span> Preset applied, but your active voice is{' '}
                <code className="font-mono text-[11px] bg-muted px-1 rounded">
                  {customVoiceInput.trim() || form.voiceId}
                </code>{' '}
                ({VOICE_FAMILY_LABELS[voiceFamily]}). Stability, Similarity Boost, Voice Style, and TTS Model
                from the preset are <strong>ignored</strong> by Telnyx for non-ElevenLabs voices.
                The preset's LLM, transcription model + language, idle handling, interruption tuning, and Voice Speed all apply normally.
                Pick an ElevenLabs voice (Browse ElevenLabs) to use the full preset.
              </div>
            )}

            {/* Live Telnyx state — shown only after user clicks Verify. This
                is the ground truth: what Telnyx has stored on each agent
                right now, fetched directly via GET /v2/ai/assistants/{id}. */}
            {liveConfig && (
              <div className="rounded-md border bg-muted/30 px-3 py-2 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">Live Telnyx agent config</span>
                  <button onClick={() => setLiveConfig(null)} className="text-[10px] text-muted-foreground hover:text-foreground">dismiss</button>
                </div>
                {liveConfig.length === 0 && (
                  <p className="text-[11px] text-muted-foreground">No Telnyx agents registered for this org.</p>
                )}
                {liveConfig.map((agent: any) => {
                  const usingCustom = form.customLlmProviderId !== null
                  const provider = usingCustom
                    ? customLlmProviders.find(p => p.id === form.customLlmProviderId)
                    : null
                  const requestedModel = usingCustom ? (provider?.defaultModelId || '(custom)') : form.llmModel
                  const storedModel = agent.live?.model
                  const storedExt = agent.live?.externalLlm
                  const modelMatches = usingCustom
                    ? (storedExt && storedExt.model === requestedModel && storedExt.baseUrl === provider?.baseUrl)
                    : storedModel === requestedModel
                  const requestedVoice = customVoiceInput.trim() || form.voiceId
                  const storedVoice = agent.live?.voice
                  return (
                    <div key={agent.assistantId} className="text-[11px] font-mono border rounded px-2 py-1.5 bg-background break-words">
                      <div className="flex items-start justify-between mb-1 gap-2 flex-wrap">
                        <span className="font-sans font-medium">{agent.name} <span className="text-muted-foreground">({agent.assistantType})</span></span>
                        {!agent.fetchedOk && <span className="text-red-600 font-sans break-words min-w-0">fetch failed: {agent.error}</span>}
                      </div>
                      {agent.fetchedOk && (
                        <div className="space-y-0.5">
                          {storedExt ? (
                            <>
                              <div className={`${modelMatches ? 'text-green-600' : 'text-red-600'} break-all`}>
                                external_llm.model: {storedExt.model || '(empty)'}{!usingCustom ? <span className="font-sans"> ✗ UI is on Bundled</span> : modelMatches ? ' ✓' : <span className="font-sans"> ✗ UI shows {requestedModel}</span>}
                              </div>
                              <div className="text-muted-foreground break-all">base_url: {storedExt.baseUrl || '(none)'}</div>
                              <div className="text-muted-foreground break-all">api_key_ref: {storedExt.apiKeyRef || '(none)'}</div>
                              {storedModel ? (
                                <div className="text-yellow-700 break-all">⚠ also has bundled model: {storedModel} (Telnyx may serve external_llm but worth confirming)</div>
                              ) : null}
                            </>
                          ) : (
                            <div className={`${modelMatches ? 'text-green-600' : 'text-red-600'} break-all`}>
                              model: {storedModel || '(none)'}{modelMatches ? ' ✓ matches UI' : <span className="font-sans"> ✗ UI shows {requestedModel}</span>}
                            </div>
                          )}
                          <div className="text-muted-foreground break-all">fallback: {agent.live?.fallbackModel || '(none)'}</div>
                          <div className="break-all">voice: {storedVoice || '(none)'}{storedVoice !== requestedVoice ? <span className="text-yellow-700 font-sans"> · UI voice: {requestedVoice}</span> : ''}</div>
                          <div className="break-words">voice_speed: {agent.live?.voiceSpeed ?? '—'} · stability: {agent.live?.stability ?? '—'} · similarity: {agent.live?.similarityBoost ?? '—'}</div>
                        </div>
                      )}
                    </div>
                  )
                })}
                <p className="text-[10px] text-muted-foreground">Fetched directly from Telnyx via GET /v2/ai/assistants/&lt;id&gt;. This is what's actually running on calls.</p>
              </div>
            )}

            {/* Voice Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <SettingLabel
                  label="Voice"
                  what="The receptionist's voice. Combined with the TTS Model below, this builds the full Telnyx voice string (ElevenLabs.eleven_flash_v2.<id>)."
                  why="Voice choice anchors the brand. Molly is our verified default; Sarah/Charlotte/Aria are premium ElevenLabs voices we've added as presets. Use 'Browse all ElevenLabs voices' to preview the full library before committing."
                  whenToChange={[
                    { symptom: 'Voice feels mismatched for the firm', action: 'Try Charlotte (calm British) or Sarah (mature professional)' },
                    { symptom: 'Want a male voice', action: 'Brian (warm) or Charlie (Australian)' },
                  ]}
                />

                <div className="flex items-center gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setVoiceLibraryOpen(true)}
                    className="h-7 text-xs"
                  >
                    <Library className="h-3 w-3 mr-1.5" />
                    Browse ElevenLabs
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setTelnyxLibraryOpen(true)}
                    className="h-7 text-xs"
                    title="Live Telnyx catalog — NaturalHD, Natural, Ultra, KokoroTTS + AWS Polly, Azure, Inworld, Minimax, Resemble, Rime, xAI"
                  >
                    <Library className="h-3 w-3 mr-1.5" />
                    Browse Telnyx library
                  </Button>
                </div>
              </div>
              {legacyVoiceWarning && (
                <div className="rounded-md border border-yellow-500/50 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 text-[11px] px-3 py-2">
                  {legacyVoiceWarning}
                </div>
              )}
              {/* When the admin pastes / picks a custom voice, the dropdown and
                  TTS Model below are inert — save() uses the custom string
                  unconditionally. Without a clear visual signal, the page looks
                  like the change didn't take. This banner makes the override
                  obvious and gives a one-click way to revert. */}
              {customVoiceInput.trim().length > 0 && (
                <div className="rounded-md border border-primary/40 bg-primary/5 px-3 py-2 flex items-center justify-between gap-3">
                  <div className="text-[11px]">
                    <span className="font-semibold">Custom voice in use:</span>{' '}
                    <code className="font-mono text-[11px] bg-muted px-1 rounded">{customVoiceInput.trim()}</code>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Overrides the dropdown + TTS Model on save. Clear to use the dropdown selection instead.</p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setCustomVoiceInput('')}
                    className="h-7 text-xs shrink-0"
                  >
                    Clear
                  </Button>
                </div>
              )}
              <Select
                value={VOICE_OPTIONS.find(v => v.id === form.voiceId)?.id ?? ''}
                onValueChange={(v) => {
                  setForm(f => ({ ...f, voiceId: v }))
                  setCustomVoiceInput('')  // dropdown selection wins over custom field
                }}
                disabled={customVoiceInput.trim().length > 0}
              >
                <SelectTrigger className={`h-9 ${customVoiceInput.trim().length > 0 ? 'opacity-50' : ''}`}>
                  <SelectValue placeholder="Pick a voice" />
                </SelectTrigger>
                <SelectContent>
                  {VOICE_OPTIONS.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name} — {v.description}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">
                {customVoiceInput.trim().length > 0
                  ? 'Inactive — custom voice above takes precedence. Clear it to re-enable this dropdown.'
                  : 'Verified voices that work end-to-end against our Telnyx + ElevenLabs integration. For anything else, use the Custom field below.'}
              </p>
            </div>

            {/* TTS Model — the ElevenLabs model that gets baked into the
                Telnyx voice string at apply time. ONLY relevant when an
                ElevenLabs voice is selected; for Rime / Telnyx-native
                voices, this control is silently ignored by the resolver
                (applyTtsModelToVoice() short-circuits on non-ElevenLabs
                strings), so we dim and label it here to make that obvious. */}
            <div className={`space-y-2 ${!isElevenLabsActive ? 'opacity-50' : ''}`}>
              <SettingLabel
                label="TTS Model (ElevenLabs only)"
                what="The ElevenLabs synthesis model embedded in voice strings of the form ElevenLabs.<this>.<voiceId>."
                why="Latency vs. multilingual tradeoff for ElevenLabs voices. Flash v2 is fastest (~75ms English-only). Flash v2.5 supports 32 languages. Turbo v2.5 is multilingual + higher quality. Has NO effect on Rime, Telnyx-native, Azure, or Inworld voices — those carry their own model in the voice ID."
                whenToChange={[
                  { symptom: 'Spanish-speaking callers struggle (ElevenLabs voice)', action: 'Switch to Flash v2.5 — OR better, swap voice to a Rime ArcanaV3 preset' },
                  { symptom: 'Want lower latency, English-only', action: 'Stay on Flash v2 (default)' },
                  { symptom: 'Tried v3 — silent audio', action: 'v3 is NOT supported on Telnyx; do not use' },
                ]}
              />
              <Select
                value={form.ttsModel}
                onValueChange={(v) => setForm(f => ({ ...f, ttsModel: v }))}
                disabled={!isElevenLabsActive}
              >
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TTS_MODEL_OPTIONS.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name} — {m.description}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!isElevenLabsActive && <NonElevenLabsNotice family={voiceFamily} customVoice={customVoiceInput.trim()} controlLabel="TTS model" />}
              {isElevenLabsActive && (
                <p className="text-[10px] text-muted-foreground">
                  eleven_v3 / v3_conversational are NOT supported on Telnyx's bridge — they produce silent audio. Stick to flash_v2 / flash_v2_5 / turbo_v2_5.
                </p>
              )}
            </div>

            {/* Custom Telnyx Voice String — escape hatch for testing voices
                from the Telnyx portal directly. Takes precedence over the
                dropdown when filled. */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Custom Telnyx Voice String <span className="text-[10px]">(optional)</span></Label>
              <Input
                value={customVoiceInput}
                onChange={(e) => setCustomVoiceInput(e.target.value)}
                placeholder="e.g. ElevenLabs.eleven_flash_v2.XXXXX or Telnyx.Ultra.UUID"
                className="h-9 font-mono text-xs"
              />
              <p className="text-[10px] text-muted-foreground">
                Paste the exact voice string from the Telnyx portal. Overrides the dropdown when non-empty. We do NOT auto-prepend "ElevenLabs." — a bare voice ID will be rejected. If you paste something Telnyx doesn't accept, the save fails with Telnyx's error message, no silent fallback.
              </p>
            </div>

            {/* LLM routing — bundled (Telnyx-managed) vs custom (external_llm) */}
            <div className="space-y-3 rounded-md border bg-muted/20 p-3">
              <SettingLabel
                label="LLM Routing"
                what="Where the AI's brain runs. Bundled = Telnyx routes to a model from their inventory using their account. Custom = Telnyx forwards to an OpenAI-compatible endpoint via your Integration Secret (lets you target gpt-5.4-mini, GLM-5.1, Kimi K2.6, OpenRouter, self-hosted, anything not in Telnyx's bundled list)."
                why="Bundled is simpler, billed via Telnyx. Custom is more flexible (newer models, your billing relationship) but adds one more failure surface — the upstream provider could go down. Telnyx's fallback_config catches that and falls back to bundled openai/gpt-4o."
                whenToChange={[
                  { symptom: 'Want a model not in Telnyx bundled list', action: 'Custom Provider' },
                  { symptom: 'Want to use your own OpenAI billing', action: 'Custom Provider with your key' },
                  { symptom: 'Performance issue, want simplest path', action: 'Bundled (one less hop)' },
                ]}
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={form.customLlmProviderId === null ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setForm(f => ({ ...f, customLlmProviderId: null }))}
                  className="flex-1"
                >
                  Bundled
                </Button>
                <Button
                  type="button"
                  variant={form.customLlmProviderId !== null ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    if (customLlmProviders.length === 0) return
                    setForm(f => ({
                      ...f,
                      customLlmProviderId: f.customLlmProviderId || customLlmProviders[0].id,
                    }))
                  }}
                  disabled={customLlmProviders.length === 0}
                  className="flex-1"
                >
                  <Cpu className="h-3.5 w-3.5 mr-1.5" />
                  Custom provider
                </Button>
              </div>

              {form.customLlmProviderId === null ? (
                <div className="space-y-2">
                  <Select value={form.llmModel} onValueChange={(v) => setForm(f => ({ ...f, llmModel: v, bundledLlmApiKeyRef: isBundledBYOK(v) ? f.bundledLlmApiKeyRef : null }))}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {/* Premium / latest — OpenAI flagship line */}
                      <SelectItem value="openai/gpt-5.2">GPT-5.2 — Latest OpenAI flagship</SelectItem>
                      <SelectItem value="openai/gpt-5.1">GPT-5.1 — OpenAI flagship</SelectItem>
                      <SelectItem value="openai/gpt-5">GPT-5 — OpenAI flagship (older)</SelectItem>
                      <SelectItem value="openai/gpt-4.1">GPT-4.1 — Reliable workhorse</SelectItem>
                      <SelectItem value="openai/gpt-4o">GPT-4o — Multilingual / reasoning</SelectItem>
                      {/* Anthropic */}
                      <SelectItem value="anthropic/claude-haiku-4-5">Claude Haiku 4.5 — Multilingual, low-latency</SelectItem>
                      {/* Google — BYOK */}
                      <SelectItem value="google/gemini-2.5-flash">Gemini 2.5 Flash — Multilingual ⚠ needs Google key</SelectItem>
                      {/* Open-weight */}
                      <SelectItem value="moonshotai/Kimi-K2.5">Kimi K2.5 — Open-weight, English-only</SelectItem>
                      <SelectItem value="Qwen/Qwen3-235B-A22B">Qwen3 235B — Open-weight, English-only</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">Bundled = Telnyx routes the model. Models marked ⚠ need a Telnyx Integration Secret with your provider's API key. For models not in this list (Kimi K2.6, GLM-5.1, GPT-5.4-mini, self-hosted, etc.), switch to Custom provider.</p>

                  {/* BYOK key picker — only shown when the selected model needs one */}
                  {isBundledBYOK(form.llmModel) && (
                    <div className="rounded-md border border-yellow-500/40 bg-yellow-500/5 p-2 mt-2 space-y-2">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <Label className="text-xs font-medium">
                          ⚠ Google API Integration Secret
                        </Label>
                        <Link
                          to="/admin/custom-llm-providers"
                          className="text-[10px] underline text-muted-foreground hover:text-foreground"
                        >
                          + Add new secret
                        </Link>
                      </div>
                      {integrationSecrets.length === 0 ? (
                        <p className="text-[11px] text-yellow-700 dark:text-yellow-400">
                          No Telnyx Integration Secrets exist on your account.{' '}
                          <Link to="/admin/custom-llm-providers" className="underline">Create one</Link>{' '}
                          first (use base URL <code className="text-[10px] bg-muted px-0.5 rounded">https://generativelanguage.googleapis.com/v1beta/openai/</code> + your Google API key).
                        </p>
                      ) : (
                        <Select
                          value={form.bundledLlmApiKeyRef || ''}
                          onValueChange={(v) => setForm(f => ({ ...f, bundledLlmApiKeyRef: v || null }))}
                        >
                          <SelectTrigger className="h-9"><SelectValue placeholder="Select a Telnyx Integration Secret" /></SelectTrigger>
                          <SelectContent>
                            {integrationSecrets.map(s => (
                              <SelectItem key={s.id} value={s.identifier}>
                                <span className="font-mono text-xs">{s.identifier}</span>
                                <span className="text-muted-foreground text-[10px] ml-2">[{s.type}]</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <p className="text-[10px] text-muted-foreground leading-relaxed">
                        Gemini routes via Telnyx but bills against your Google API key. Pick the secret that holds your Google key — if you only see <code className="text-[10px]">elevenlabs</code> or other unrelated secrets, create a new one via "Add new secret" above.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {customLlmProviders.length === 0 ? (
                    <div className="rounded-md border border-yellow-500/50 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 text-[11px] px-3 py-2">
                      No custom providers configured.{' '}
                      <Link to="/admin/custom-llm-providers" className="underline">
                        Add one
                      </Link>{' '}
                      first.
                    </div>
                  ) : (
                    <Select
                      value={form.customLlmProviderId || ''}
                      onValueChange={(v) => setForm(f => ({ ...f, customLlmProviderId: v }))}
                    >
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {customLlmProviders.map(p => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} — <span className="font-mono">{p.defaultModelId}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <p className="text-[10px] text-muted-foreground">
                    Routes through Telnyx's <code className="text-[10px] bg-muted px-0.5 rounded">external_llm</code>{' '}
                    using the provider's configured base URL + Integration Secret. Manage providers in{' '}
                    <Link to="/admin/custom-llm-providers" className="underline">Custom LLMs</Link>.
                  </p>
                </div>
              )}
            </div>

            {/* Voice Quality Sliders — Voice Speed applies to ALL voice families
                (buildVoiceSettings emits voice_speed unconditionally). Stability
                and Similarity Boost are ElevenLabs-only; for Telnyx native /
                Rime / AWS / Azure voices, Telnyx ignores them. We dim those two
                so admins testing a NaturalHD voice don't waste time tuning
                knobs that have no effect. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`space-y-2 ${!isElevenLabsActive ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between">
                  <SettingLabel
                    label="Stability (ElevenLabs only)"
                    what="How consistent the voice is across utterances. Lower = more expressive variation, quicker pace. Higher = consistent, robotic-leaning."
                    why="Too low and the voice sounds erratic call-to-call. Too high and it sounds like a script reader. 0.4-0.6 is the sweet spot for receptionist."
                    whenToChange={[
                      { symptom: "Voice sounds robotic / monotone", action: 'Lower to 0.4' },
                      { symptom: "Voice sounds erratic / unstable across calls", action: 'Raise to 0.6' },
                    ]}
                  />
                  <span className="text-xs text-muted-foreground font-mono">{form.stability.toFixed(2)}</span>
                </div>
                <input type="range" min={0} max={1} step={0.05} value={form.stability}
                  onChange={(e) => setForm(f => ({ ...f, stability: parseFloat(e.target.value) }))}
                  disabled={!isElevenLabsActive}
                  className="w-full accent-primary" />
                {isElevenLabsActive
                  ? <p className="text-[10px] text-muted-foreground">Lower = expressive, quicker pace. Higher = consistent.</p>
                  : <NonElevenLabsNotice family={voiceFamily} customVoice={customVoiceInput.trim()} controlLabel="Stability" />}
              </div>
              <div className={`space-y-2 ${!isElevenLabsActive ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between">
                  <SettingLabel
                    label="Similarity Boost (ElevenLabs only)"
                    what="How closely output matches the underlying voice clone. 1.0 reproduces the clone verbatim and tends to sound rigid; 0.55–0.75 is more natural."
                    why="Affects timbre and cadence. Low values let the model interpret the voice more freely — usually warmer. High values are 'truer' to the clone but can feel locked in."
                    whenToChange={[
                      { symptom: "Voice sounds rigid / unnatural", action: 'Lower toward 0.55' },
                      { symptom: "Voice drifts from brand", action: 'Raise toward 0.75' },
                    ]}
                  />
                  <span className="text-xs text-muted-foreground font-mono">{form.similarityBoost.toFixed(2)}</span>
                </div>
                <input type="range" min={0} max={1} step={0.05} value={form.similarityBoost}
                  onChange={(e) => setForm(f => ({ ...f, similarityBoost: parseFloat(e.target.value) }))}
                  disabled={!isElevenLabsActive}
                  className="w-full accent-primary" />
                {isElevenLabsActive
                  ? <p className="text-[10px] text-muted-foreground">How closely output matches voice clone. 1.0 sounds rigid — 0.55–0.75 is natural.</p>
                  : <NonElevenLabsNotice family={voiceFamily} customVoice={customVoiceInput.trim()} controlLabel="Similarity Boost" />}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <SettingLabel
                    label="Voice Speed"
                    what="Playback rate of the synthesized speech. Applies to every voice family — Telnyx accepts voice_speed on all providers."
                    why="A receptionist that speaks too slow feels patronizing; too fast feels rushed and increases ASR errors when the caller talks back. 1.0 is natural conversational pace."
                    whenToChange={[
                      { symptom: 'Callers say "she talks too fast"', action: 'Lower to 0.95' },
                      { symptom: 'Callers say "she sounds slow"', action: 'Raise to 1.05-1.1' },
                    ]}
                  />
                  <span className="text-xs text-muted-foreground font-mono">{form.voiceSpeed.toFixed(2)}×</span>
                </div>
                <input type="range" min={0.5} max={1.5} step={0.05} value={form.voiceSpeed}
                  onChange={(e) => setForm(f => ({ ...f, voiceSpeed: parseFloat(e.target.value) }))}
                  className="w-full accent-primary" />
                <p className="text-[10px] text-muted-foreground">Playback rate. 1.0 = natural, 1.1 = snappy sales pace. Works for all voice families.</p>
              </div>
            </div>

            {/* Interruption / turn-taking sliders — direct mapping to Telnyx
                interruption_settings. These are the knobs that control how
                quickly the AI takes a turn after the caller speaks. */}
            <div className="space-y-3 rounded-md border bg-muted/20 p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <SettingLabel
                  label="Interruption / Turn-Taking"
                  what="How the AI decides the caller is done talking. Master switch + three timing knobs."
                  why="Bad turn-taking is the #1 thing that makes a voice agent feel broken. Too eager and the AI talks over coughs and 'um's. Too patient and it feels like an answering machine. Disable entirely (toggle below) when interruption misfires — common with multilingual where Spanish pause-fillers ('este…', 'pues…') trip the gate."
                  whenToChange={[
                    { symptom: 'AI cuts off mid-thought', action: 'Raise wait to 1.3s' },
                    { symptom: 'AI feels slow / unresponsive', action: 'Lower wait to 0.7s' },
                    { symptom: "AI doesn't realize caller stopped", action: 'Lower trailing to 1.2s' },
                    { symptom: 'AI cut off on Spanish pause-fillers', action: 'Disable interruption entirely (toggle)' },
                  ]}
                />
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Allow Interruption</Label>
                  <Button
                    type="button"
                    variant={form.interruptionEnabled ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setForm(f => ({ ...f, interruptionEnabled: !f.interruptionEnabled }))}
                    className="shrink-0"
                  >
                    {form.interruptionEnabled ? 'ON' : 'OFF'}
                  </Button>
                </div>
              </div>
              {!form.interruptionEnabled && (
                <p className="text-[11px] text-yellow-700 dark:text-yellow-400 leading-relaxed">
                  ⚠ Interruption disabled. Caller must wait for AI to finish each utterance — no mid-utterance yield. Useful for multilingual debugging or outbound pitch flow. Sliders below are sent but ignored by Telnyx until re-enabled.
                </p>
              )}
              <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${!form.interruptionEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Wait Before Speaking</Label>
                    <span className="text-xs text-muted-foreground font-mono">{form.interruptionWaitSeconds.toFixed(2)}s</span>
                  </div>
                  <input type="range" min={0.1} max={3.0} step={0.1} value={form.interruptionWaitSeconds}
                    onChange={(e) => setForm(f => ({ ...f, interruptionWaitSeconds: parseFloat(e.target.value) }))}
                    disabled={!form.interruptionEnabled}
                    className="w-full accent-primary" />
                  <p className="text-[10px] text-muted-foreground">How long AI waits after caller pauses. Lower = snappy. Higher = filters coughs/clicks.</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">End Turn (Punctuated)</Label>
                    <span className="text-xs text-muted-foreground font-mono">{form.interruptionOnPunctuationSeconds.toFixed(2)}s</span>
                  </div>
                  <input type="range" min={0} max={2.0} step={0.05} value={form.interruptionOnPunctuationSeconds}
                    onChange={(e) => setForm(f => ({ ...f, interruptionOnPunctuationSeconds: parseFloat(e.target.value) }))}
                    disabled={!form.interruptionEnabled}
                    className="w-full accent-primary" />
                  <p className="text-[10px] text-muted-foreground">Pause after caller's punctuation (?,.,!). Telnyx default 0.10.</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">End Turn (Trailing)</Label>
                    <span className="text-xs text-muted-foreground font-mono">{form.interruptionOnNoPunctuationSeconds.toFixed(2)}s</span>
                  </div>
                  <input type="range" min={0.3} max={4.0} step={0.1} value={form.interruptionOnNoPunctuationSeconds}
                    onChange={(e) => setForm(f => ({ ...f, interruptionOnNoPunctuationSeconds: parseFloat(e.target.value) }))}
                    disabled={!form.interruptionEnabled}
                    className="w-full accent-primary" />
                  <p className="text-[10px] text-muted-foreground">Pause when caller trails off (no punctuation). Default 1.5 inbound / 2.0 outbound.</p>
                </div>
              </div>
            </div>

            {/* Voice Style — ElevenLabs expressiveness 0-1. Pairs with
                stability/similarity. Higher style = warmer/more dramatic;
                lower = neutral/consistent. Sweet spot for receptionist 0-0.2. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md border bg-muted/20 p-3">
              <div className={`space-y-2 ${!isElevenLabsActive ? 'opacity-50' : ''}`}>
                <div className="flex items-center justify-between">
                  <SettingLabel
                    label="Voice Style (ElevenLabs only)"
                    what="ElevenLabs expressiveness 0-1. Pairs with stability — together they shape how 'alive' the voice sounds."
                    why="A receptionist voice that's too neutral feels robotic; too dramatic feels unprofessional. 0.10–0.20 is the sweet spot. Patched onto voice_settings only when active voice is ElevenLabs (TelnyxProvider line ~919); silently dropped otherwise."
                    whenToChange={[
                      { symptom: "Callers say 'sounds like a robot'", action: 'Raise to 0.20-0.25' },
                      { symptom: "Voice sounds theatrical / over-acted", action: 'Drop to 0.05' },
                    ]}
                  />
                  <span className="text-xs text-muted-foreground font-mono">{form.voiceStyle.toFixed(2)}</span>
                </div>
                <input type="range" min={0} max={1} step={0.05} value={form.voiceStyle}
                  onChange={(e) => setForm(f => ({ ...f, voiceStyle: parseFloat(e.target.value) }))}
                  disabled={!isElevenLabsActive}
                  className="w-full accent-primary" />
                {isElevenLabsActive
                  ? <p className="text-[10px] text-muted-foreground">0 = neutral/consistent · 0.2 = warm receptionist · 0.5+ = dramatic, less stable</p>
                  : <NonElevenLabsNotice family={voiceFamily} customVoice={customVoiceInput.trim()} controlLabel="Voice Style" />}
              </div>
              <div className="space-y-2">
                <SettingLabel
                  label="Transcription Model (ASR)"
                  what="The speech-to-text model that converts what the caller says into text the LLM sees."
                  why="Bad ASR is invisible to you but devastating to the conversation — the LLM 'hears' garbled words and responds wrong. For multilingual, Telnyx specifically recommends Whisper Large v3 Turbo. Nova 3 is also multilingual but tends to lock to one language at start of call."
                  whenToChange={[
                    { symptom: 'ASR misses Spanish or AI won\'t respond in Spanish', action: 'Whisper Large v3 Turbo + language Auto' },
                    { symptom: 'Need maximum accuracy', action: 'Whisper Large v3 Turbo' },
                    { symptom: 'English-only, latency-sensitive', action: 'Flux + language English' },
                  ]}
                />
                <Select value={form.transcriptionModel} onValueChange={(v) => setForm(f => ({ ...f, transcriptionModel: v }))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRANSCRIPTION_OPTIONS.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name} — {m.description}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">Affects how well AI hears callers. For ES/EN code-switching set Language Mode below to Auto.</p>

                {/* Transcription language mode — critical for multilingual.
                    'auto' lets the ASR detect language per utterance; 'en' or
                    'es' locks to one language. Hidden bug we just hit: when
                    admin picks a multilingual model but language stays 'en',
                    Spanish gets transcribed as garbled English, the LLM sees
                    nonsense, and 'won't respond in Spanish.' */}
                <div className="pt-1">
                  <Label className="text-xs">Language Mode</Label>
                  <div className="flex gap-1.5 mt-1.5">
                    <Button
                      type="button"
                      variant={form.transcriptionLanguage === 'auto' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setForm(f => ({ ...f, transcriptionLanguage: 'auto' }))}
                      className="flex-1 h-8 text-xs"
                    >
                      Auto-detect
                    </Button>
                    <Button
                      type="button"
                      variant={form.transcriptionLanguage === 'en' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setForm(f => ({ ...f, transcriptionLanguage: 'en' }))}
                      className="flex-1 h-8 text-xs"
                    >
                      English only
                    </Button>
                    <Button
                      type="button"
                      variant={form.transcriptionLanguage === 'es' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setForm(f => ({ ...f, transcriptionLanguage: 'es' }))}
                      className="flex-1 h-8 text-xs"
                    >
                      Spanish only
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Auto = transcriber detects language per utterance (required for ES/EN code-switching). English/Spanish-only = locked to that language, slightly faster but breaks any other language input.
                  </p>
                  {/* ASR / language combo sanity check. flux is English-only,
                      so flux + auto can't actually auto-detect anything but
                      English — it'll mistranscribe Spanish as garbled English
                      and the LLM will respond confused. Symmetric warning for
                      a multilingual model + locked single language: admin
                      paid for the multilingual ASR but locked it to one
                      tongue, so they're getting no benefit. */}
                  {form.transcriptionModel === 'deepgram/flux' && form.transcriptionLanguage !== 'en' && (
                    <p className="text-[10px] text-yellow-700 dark:text-yellow-400 mt-1 leading-relaxed">
                      ⚠ Mismatch: Deepgram Flux is English-only. With Language Mode set to{' '}
                      <strong>{form.transcriptionLanguage === 'auto' ? 'Auto-detect' : 'Spanish only'}</strong>,
                      the ASR will mistranscribe non-English speech as garbled English. Either pick Whisper Large v3 Turbo / Deepgram Nova 3 / Azure Fast, or switch Language Mode to English only.
                    </p>
                  )}
                  {form.transcriptionModel !== 'deepgram/flux' &&
                    ['openai/whisper-large-v3-turbo', 'deepgram/nova-3', 'deepgram/nova-2', 'azure/fast'].includes(form.transcriptionModel) &&
                    form.transcriptionLanguage !== 'auto' && (
                    <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
                      Note: you've picked a multilingual ASR model but locked Language Mode to{' '}
                      <strong>{form.transcriptionLanguage === 'en' ? 'English only' : 'Spanish only'}</strong>.
                      Code-switching callers won't be transcribed correctly. Switch to Auto-detect if you take bilingual calls.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Idle handling + call limits — when AI prompts/hangs up on
                silent callers, plus a max-call-duration safety net. */}
            <div className="space-y-3 rounded-md border bg-muted/20 p-3">
              <SettingLabel
                label="Idle Handling & Call Limits"
                what="Three safety knobs: when the AI prompts a silent caller, when it hangs up on a silent caller, and the absolute maximum call duration."
                why="Silent callers happen — distractions, technical issues, or callers thinking. Without these, calls either feel awkward (long silence) or end too aggressively (frustrating). Time limit is your last line of defense against runaway LLM costs."
                whenToChange={[
                  { symptom: 'Callers think AI froze (long pauses)', action: 'Lower reply-after-silence to 10s' },
                  { symptom: 'AI hangs up before nervous callers respond', action: 'Raise hang-up to 120s' },
                  { symptom: 'Outbound calls running long', action: 'Lower max duration to 600s (10min)' },
                ]}
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Reply After Silence</Label>
                    <span className="text-xs text-muted-foreground font-mono">{form.userIdleReplySeconds}s</span>
                  </div>
                  <input type="range" min={3} max={120} step={1} value={form.userIdleReplySeconds}
                    onChange={(e) => setForm(f => ({ ...f, userIdleReplySeconds: parseInt(e.target.value, 10) }))}
                    className="w-full accent-primary" />
                  <p className="text-[10px] text-muted-foreground">When AI says "Are you still there?" Default 15s. Lower = pushy.</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Hang Up On Silence</Label>
                    <span className="text-xs text-muted-foreground font-mono">{form.userIdleTimeoutSeconds}s</span>
                  </div>
                  <input type="range" min={10} max={600} step={10} value={form.userIdleTimeoutSeconds}
                    onChange={(e) => setForm(f => ({ ...f, userIdleTimeoutSeconds: parseInt(e.target.value, 10) }))}
                    className="w-full accent-primary" />
                  <p className="text-[10px] text-muted-foreground">When AI ends a silent call. Default 90s.</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">Max Call Duration</Label>
                    <span className="text-xs text-muted-foreground font-mono">{Math.round(form.timeLimitSeconds / 60)}min</span>
                  </div>
                  <input type="range" min={60} max={3600} step={60} value={form.timeLimitSeconds}
                    onChange={(e) => setForm(f => ({ ...f, timeLimitSeconds: parseInt(e.target.value, 10) }))}
                    className="w-full accent-primary" />
                  <p className="text-[10px] text-muted-foreground">Safety cap. Default 30min. Receptionist calls rarely exceed 5min.</p>
                </div>
              </div>
            </div>

            {/* Cross-call memory — privacy/personalization toggle. */}
            <div className="flex items-start justify-between gap-4 rounded-md border bg-muted/20 p-3">
              <div>
                <SettingLabel
                  label="Cross-Call Memory"
                  what='When ON, AI uses Telnyx insight memory to recognize returning callers by phone number. "Welcome back, Sarah — we spoke last Tuesday about the LLC filing."'
                  why="For receptionist personas, this is a huge UX win on returning callers — feels personal, saves them re-explaining context. For privacy-sensitive tenants (medical, divorce intake) you may want each call to start fresh."
                  whenToChange={[
                    { symptom: "Tenant has compliance / privacy concerns", action: 'Turn OFF' },
                    { symptom: "Repeat callers complain AI doesn't remember them", action: 'Turn ON' },
                  ]}
                />
                <p className="text-[10px] text-muted-foreground mt-1">When ON, AI uses Telnyx insight memory to recognize returning callers by phone number ("Welcome back, Sarah — we spoke last Tuesday about the LLC filing"). Turn OFF for privacy-sensitive tenants where each call should start fresh.</p>
              </div>
              <Button
                type="button"
                variant={form.crossCallMemoryEnabled ? 'default' : 'outline'}
                size="sm"
                onClick={() => setForm(f => ({ ...f, crossCallMemoryEnabled: !f.crossCallMemoryEnabled }))}
                className="shrink-0"
              >
                {form.crossCallMemoryEnabled ? 'ON' : 'OFF'}
              </Button>
            </div>

            {/* Recent calls panel — pulls model attribution + LLM TTFT directly
                from Telnyx so the admin can A/B compare bundled vs custom-LLM
                without leaving the dashboard. */}
            <div className="space-y-2 rounded-md border bg-muted/20 p-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs flex items-center gap-1.5">
                  <Activity className="h-3.5 w-3.5" /> Recent Calls — Model & Latency
                </Label>
                <Button variant="outline" size="sm" onClick={loadRecentCalls} disabled={isLoadingRecentCalls}>
                  {isLoadingRecentCalls ? 'Loading…' : (recentCalls ? 'Refresh' : 'Load last 10 calls')}
                </Button>
              </div>
              {recentCalls && recentCalls.length === 0 && (
                <p className="text-[11px] text-muted-foreground">No recent calls with Telnyx conversation IDs.</p>
              )}
              {recentCalls && recentCalls.length > 0 && (
                <div className="space-y-1">
                  {recentCalls.map((call) => {
                    const ttftClass = call.llmTtftMs == null ? 'text-muted-foreground' :
                      call.llmTtftMs < 600 ? 'text-green-600' :
                      call.llmTtftMs < 1000 ? 'text-yellow-700' :
                      'text-red-600'
                    const modelClass = call.modelKind === 'custom_llm' ? 'text-blue-600 font-semibold' :
                      call.modelKind === 'bundled' ? 'text-green-600 font-semibold' :
                      'text-muted-foreground'
                    return (
                      <div key={call.callId} className="text-[11px] border rounded px-2 py-1.5 bg-background space-y-1 sm:space-y-0">
                        {/* Row 1 (mobile + desktop): timestamp · duration · phone */}
                        <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
                          <span className="shrink-0">
                            {new Date(call.startedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          </span>
                          <span className="shrink-0">{call.durationSeconds ?? '?'}s</span>
                          {call.phoneFrom && <span className="font-mono truncate">{call.phoneFrom}</span>}
                        </div>
                        {/* Row 2: model name (large, color-coded) + TTFT (color-coded). Same row on desktop, stack on mobile. */}
                        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-x-3 gap-y-0.5">
                          <div className="flex items-baseline gap-2 min-w-0">
                            <span className={`${modelClass} font-mono break-all`}>
                              {call.modelUsed || '(unknown)'}
                            </span>
                            <span className="text-[10px] text-muted-foreground shrink-0">[{call.modelKind}]</span>
                          </div>
                          <span className={`${ttftClass} font-mono text-[11px] shrink-0`}>
                            TTFT {call.llmTtftMs ?? '—'}{call.llmTtftMs != null ? 'ms' : ''}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                  <p className="text-[10px] text-muted-foreground pt-1 leading-relaxed">
                    Green = bundled (Telnyx-routed). Blue = custom_llm (your provider). TTFT &lt; 600ms is excellent for voice; 600-1000ms is acceptable; &gt; 1000ms feels laggy on call.
                  </p>
                </div>
              )}
              {!recentCalls && !isLoadingRecentCalls && (
                <p className="text-[10px] text-muted-foreground">Pulls each call's assistant version from Telnyx, resolves which model served it (bundled vs external_llm), and shows the LLM TTFT — so you can compare performance side by side after switching providers.</p>
              )}
            </div>

            </VoiceEngineTooltipScope>
          </CardContent>
        </CollapsibleContent>
      </Card>
      <VoiceLibraryModal
        open={voiceLibraryOpen}
        onOpenChange={setVoiceLibraryOpen}
        currentVoiceId={customVoiceInput.trim() || form.voiceId}
        ttsModel={form.ttsModel}
        onSelect={(telnyxVoiceString) => {
          // Drop the selected voice into the custom-voice field — it takes
          // precedence over the dropdown on save and is the right field for
          // a full Telnyx voice string.
          setCustomVoiceInput(telnyxVoiceString)
          toast({
            title: 'Voice selected',
            description: 'Click "Save & Sync All Agents" to push this voice to Telnyx.',
          })
        }}
      />
      <TelnyxVoiceLibraryModal
        open={telnyxLibraryOpen}
        onOpenChange={setTelnyxLibraryOpen}
        currentVoiceId={customVoiceInput.trim() || form.voiceId}
        onSelect={(telnyxVoiceString) => {
          // Telnyx voice strings already contain the full Provider.Model.id
          // path, so they go straight into the custom-voice field unchanged.
          setCustomVoiceInput(telnyxVoiceString)
          toast({
            title: 'Voice selected',
            description: 'Click "Save & Sync All Agents" to push this voice to Telnyx.',
          })
        }}
      />
    </Collapsible>
  )
}
